/**
 * D-EZ-111 — THE EX-DRIVER IN THE BOOTH: HIS PICK, HIS PAST, HIS GRUDGES.
 *
 * A real analyst has opinions. Before the green he picks somebody, and later he
 * owns it — "there's my pick", "so much for my pick", and next week "I had
 * Ballew at Atlanta and he ran ninth". That is the single cheapest thing that
 * makes a booth feel like people rather than a ticker.
 *
 * THE RULES IT KEEPS:
 *   • A pick is OPINION, hedged, and is built only from what is on screen before
 *     the green: the starting grid and the league's memory going in (standings,
 *     streaks, track history — `BoothSeasonContext`, history up to the previous
 *     race). It never reads the race it is about.
 *   • It is a pure function of those inputs and the league key — so last week's
 *     pick can be re-derived from last week's inputs, and nothing is stored.
 *   • His own past (wins, seasons, the old rival) is fiction about HIM, fixed in
 *     the cast table (`cast.ts`), and every number he cites comes from there.
 */

import type { AnalystPast, Cast } from './cast.ts';
import type { BoothSeasonContext } from './context.ts';
import type { Fact } from './researcher.ts';
import { hash as stableHash } from './kit.ts';
import { cap, fact, fillSlots, ordWord, plural, rotate, surname } from './kit.ts';

export type PickReason =
  | 'pole'
  | 'front-row'
  | 'defending'
  | 'track-wins'
  | 'points-leader'
  | 'win-streak'
  | 'top5-streak'
  | 'first-win'
  | 'drought'
  | 'bubble'
  | 'champion';

export interface AnalystPick {
  driverId: string;
  name: string;
  /** Strongest reason first. */
  reasons: PickReason[];
  /** One or two sentences: why, in his words. Every number is in `facts`. */
  why: string;
  facts: Fact[];
}

interface Candidate {
  id: string;
  score: number;
  reasons: { reason: PickReason; weight: number; why: string; facts: Fact[] }[];
}

const LEAN_BONUS: Record<AnalystPast['lean'], Partial<Record<PickReason, number>>> = {
  veterans: { defending: 3, 'track-wins': 3, champion: 3 },
  form: { 'points-leader': 4, 'win-streak': 3, 'top5-streak': 3 },
  underdogs: { 'first-win': 5, drought: 3, bubble: 4 },
  qualifying: { pole: 4, 'front-row': 3 },
};

/**
 * THE PICK. `order` is the starting grid (ids, pole first) — known before the
 * green; `season` is the league's memory going in (null for an exhibition).
 */
export function analystPick(
  cast: Cast,
  season: BoothSeasonContext | null | undefined,
  order: readonly string[],
  nameOf: (id: string) => string,
  key: string,
): AnalystPick | null {
  if (order.length === 0) return null;
  const grid = new Map(order.map((id, i) => [id, i + 1]));
  const lean = LEAN_BONUS[cast.past.lean];
  const cands = new Map<string, Candidate>();
  const add = (id: string, reason: PickReason, base: number, why: string, facts: Fact[]) => {
    if (!grid.has(id)) return;
    const c = cands.get(id) ?? { id, score: 0, reasons: [] };
    c.reasons.push({ reason, weight: base + (lean[reason] ?? 0), why, facts });
    cands.set(id, c);
  };
  const n = (id: string) => nameOf(id);

  add(order[0], 'pole', 3, "He's on the pole.", [fact('race-field', `${n(order[0])} starts on the pole`, 1, [order[0]])]);
  if (order[1]) add(order[1], 'front-row', 1.5, 'He rolls off from the front row.', [fact('race-field', `${n(order[1])} starts second`, 2, [order[1]])]);
  if (season) {
    const d = season.defendingRaceWinner;
    if (d) add(d.driverId, 'defending', 4, `He won this race in season ${d.seasonNumber}.`, [fact('history', `${d.name} won this race in season ${d.seasonNumber}`, d.seasonNumber, [d.driverId])]);
    for (const w of season.trackWinners.slice(0, 4)) {
      if (w.wins >= 2) add(w.driverId, 'track-wins', 3 + w.wins / 2, `He's won here ${w.wins} times.`, [fact('history', `${w.name} has ${plural(w.wins, 'win')} at this track`, w.wins, [w.driverId])]);
    }
    const L = season.pointsLeader;
    if (L && season.raceNumber > 2) {
      add(L.driverId, 'points-leader', 4,
        L.marginToSecond > 0 ? `He comes in leading the points by ${L.marginToSecond}.` : "He's tied for the points lead coming in.",
        [fact('season', `${L.name} leads the points by ${L.marginToSecond}`, L.marginToSecond, [L.driverId])]);
    }
    for (const s of season.activeStreaks) {
      if (s.kind === 'wins') add(s.driverId, 'win-streak', 5, `He's won ${s.length} straight.`, [fact('history', `${s.name} has won ${s.length} in a row`, s.length, [s.driverId])]);
      if (s.kind === 'top5') add(s.driverId, 'top5-streak', 3, `He's got ${s.length} straight top-five finishes.`, [fact('history', `${s.name}: ${s.length} straight top-five finishes`, s.length, [s.driverId])]);
    }
    for (const m of season.milestonesInPlay) {
      const at = grid.get(m.driverId);
      if (at === undefined) continue;
      if (m.kind === 'first-win' && m.n >= 10 && at <= 10) {
        add(m.driverId, 'first-win', 3, `He's ${m.n} starts in without a win, and he rolls off ${ordWord(at)}. He's due.`, [
          fact('history', `${m.name} is winless in ${m.n} starts`, m.n, [m.driverId]),
          fact('race-field', `${m.name} starts ${ordWord(at)}`, at, [m.driverId]),
        ]);
      }
      if (m.kind === 'drought' && m.n >= 15 && at <= 12) {
        add(m.driverId, 'drought', 2, `He hasn't won in ${m.n} starts, and he rolls off ${ordWord(at)}.`, [
          fact('history', `${m.name} has gone ${m.n} starts without a win`, m.n, [m.driverId]),
          fact('race-field', `${m.name} starts ${ordWord(at)}`, at, [m.driverId]),
        ]);
      }
    }
    const p = season.playoff;
    if (p?.phase === 'regular-season' && p.racesToCut <= 10) {
      const out = p.bubble.find((b) => b.side === 'out' && (grid.get(b.driverId) ?? 99) <= 15);
      if (out) add(out.driverId, 'bubble', 3, `He's ${plural(-out.gap, 'point')} outside the playoff cut, and he needs a run.`, [
        fact('season', `${out.name} is ${-out.gap} points outside the playoff cut`, -out.gap, [out.driverId]),
      ]);
    }
    const c = season.defendingChampion;
    if (c) add(c.driverId, 'champion', 3, "He's the defending champion.", [fact('season', `${c.name} is the defending champion`, c.seasonNumber, [c.driverId])]);
  }

  let best: Candidate | null = null;
  let bestScore = -1;
  for (const c of cands.values()) {
    const top = [...c.reasons].sort((a, b) => b.weight - a.weight);
    c.reasons = top;
    // Two reasons count, the second at half — a man with two good reasons beats a man with one great one only narrowly.
    const score = top[0].weight + (top[1]?.weight ?? 0) / 2 + (stableHash(`pick|${key}|${c.id}`) % 100) / 100;
    if (score > bestScore) {
      best = c;
      bestScore = score;
    }
  }
  if (!best) return null;
  // Two reasons, unless the first already says where he starts (then the pole is the same fact twice).
  const top2 = best.reasons.slice(0, 2);
  const used = top2.some((r) => /rolls off/.test(r.why)) ? top2.filter((r) => r.reason !== 'pole' && r.reason !== 'front-row') : top2;
  return {
    driverId: best.id,
    name: nameOf(best.id),
    reasons: used.map((r) => r.reason),
    why: used.map((r) => r.why).join(' '),
    facts: used.flatMap((r) => r.facts),
  };
}

// ---------------------------------------------------------------------------
// What he says
// ---------------------------------------------------------------------------

/** The take, hedged. `{name}` is the pick, `{why}` the reasons. */
const TAKE = [
  "I'll take {name} today. {why}",
  "Give me {name}. {why} It may not go his way, but that's my pick.",
  'My pick is {name}. {why}',
  'I like {name} here. {why}',
  'If you made me choose, {name}. {why}',
  'Put me down for {name}. {why}',
  "{name} is my pick. {why} We'll see if he makes me look smart.",
  "I'm going with {name}. {why}",
  'I keep coming back to {name}. {why}',
  'You want a name? {name}. {why}',
  "For my money, {name}. {why} Nothing's promised out there.",
  'I have {name} circled. {why}',
];

const LEAN_WORDS: Record<AnalystPast['lean'], string[]> = {
  veterans: ['I always lean on a man who has done it before.', 'Experience wins more of these than people think.'],
  form: ['Ride the hot hand, I always say.', 'I go with whoever is running well, and I do not overthink it.'],
  underdogs: ['I like a driver with something to prove.', 'Give me the hungry one every time.'],
  qualifying: ['Track position is everything to me.', 'I won from the front more than from anywhere else.'],
};

/** The same man two weeks running. */
const AGAIN = [
  "I'm sticking with {name}. {why}",
  'Same pick as last week: {name}. {why}',
  "I'm going back to {name}. {why}",
  '{name} again for me. {why} Stubborn, maybe.',
  "Call me loyal. It's {name} again. {why}",
  "Why change now? I'll ride with {name}. {why}",
  "Back to the well: {name}. {why}",
];

/** The pick is a man the booth just set up as a storyline: say so, don't repeat why. */
const TAKE_STORY = [
  "And that's my pick: {name}.",
  "{name} is the man I'm taking today, for exactly that reason.",
  "I'll go with {name}. You just heard why.",
  "That's who I like today. {name}.",
  "Put me down for {name}, and that's not a coincidence.",
];

export function takeLine(cast: Cast, pick: AnalystPick, key: string, seq: number, again = false, told = false): { text: string; tpl: string } {
  if (told) {
    const t = rotate(TAKE_STORY, `take-story|${key}|${pick.driverId}`, seq);
    return { text: t.item.replace('{name}', pick.name), tpl: `take-story#${t.index}` };
  }
  const bank = again ? AGAIN : TAKE;
  const t = rotate(bank, `take|${key}|${pick.driverId}`, seq);
  let text = t.item.replace('{name}', pick.name).replace('{why}', pick.why);
  const leanWords = LEAN_WORDS[cast.past.lean];
  const h = stableHash(`lean|${key}|${seq}`);
  if (h % 4 === 0) text += ` ${leanWords[(h >>> 4) % leanWords.length]}`;
  return { text, tpl: `${again ? 'take-again' : 'take'}#${t.index}` };
}

/** Last week, owned. Only from a finished race's recorded result. */
export function lastPickLine(
  pick: { name: string; place: number | null; out: boolean; won: boolean; raceName: string },
  key: string,
  seq: number,
): { text: string; tpl: string; facts: Fact[] } {
  const s = pick.name;
  const facts = [fact('take', `last week the analyst picked ${pick.name} in the ${pick.raceName}; he finished ${pick.place ?? 'out'}`, pick.place ?? 0)];
  let bank: string[];
  if (pick.won) bank = [
    "Last week I had {name} in the {race}, and he won it. I'll take a bow.",
    'I picked {name} last week and he delivered. One for one is a good place to be.',
    "You may remember I had {name} last week. He won. I'm just saying.",
    'Last week, {name}. Won it. I would like that noted.',
    "My pick last week was {name}, and he's the one who won the {race}.",
  ];
  else if (pick.out) bank = [
    "Last week I had {name} in the {race}, and he didn't make the finish. That one's on me.",
    'My pick last week, {name}, was out before the end. Moving on.',
    'I picked {name} last week and he never saw the checkered flag. New week.',
    "We don't need to talk about last week's pick. {name}, out of the race.",
    'Last week I went with {name}, and he was out early. It happens.',
  ];
  else if ((pick.place ?? 99) <= 5) bank = [
    "I had {name} last week and he ran {pos}. Close enough that I'm not apologizing.",
    'Last week my pick, {name}, came home {pos}. Not bad.',
    "{name} ran {pos} for me last week. I'll take that.",
    'My man {name} ran {pos} last week. Knocking on the door.',
    'Last week I took {name} and he gave me a {pos}-place run.',
  ];
  else bank = [
    'Last week I had {name} in the {race}. He ran {pos}. So much for that.',
    'My pick last week, {name}, finished {pos}. You win some.',
    "{name} ran {pos} for me last week, and I've heard about it all week.",
    'Last week I picked {name}. {Pos}. We move on.',
    'I took {name} last week and he finished {pos}. Not my finest hour.',
  ];
  const t = rotate(bank, `lastpick|${key}|${pick.name}`, seq);
  const text = t.item
    .replace('{name}', pick.name)
    .replace('{s}', s)
    .replace('{race}', pick.raceName)
    .replace('{pos}', pick.place ? ordWord(pick.place) : 'out')
    .replace('{Pos}', pick.place ? cap(ordWord(pick.place)) : 'Out');
  return { text, tpl: `lastpick#${pick.won ? 'w' : pick.out ? 'o' : 'p'}${t.index}`, facts };
}

/** His pick, mid-race: leading, or gone. */
export function pickNow(kind: 'leads' | 'out' | 'wins' | 'beaten', name: string, place: number | null, key: string, seq: number, short?: string): { text: string; tpl: string } {
  const s = short ?? surname(name);
  const BANK: Record<typeof kind, string[]> = {
    leads: ["There's my pick. {s}'s out front.", "That's the man I picked before the green.", 'I said {s} would be a factor today. There he is.', "My pick, leading the race. I'll try to stay humble.", 'Look who it is. My pick, {s}, out front.', 'I had {s} before the green. Just saying.', '{s} leads, and I picked him. Somebody write that down.'],
    out: ['And there goes my pick.', "So much for my pick. {s}'s done for the day.", "That's my pick out of it. Hard to watch.", "Well, I picked {s}. That's the end of that.", 'There went my afternoon. {s} is out.', 'That hurts. My pick, {s}, is done.', "Not the way I drew it up. {s}'s out of it."],
    wins: ['Called it. I picked {s} before the green.', "That's my pick in victory lane. I won't say I told you so. I told you so.", 'I had {s} from the start. Good day to be me.', 'My pick wins it. Write that down somewhere.'],
    beaten: ['My pick, {name}, finished {pos}.', 'I had {name}. He came home {pos}.', '{name}, my pick, ran {pos} today.', 'For the record, I picked {name}. {Pos} at the line.'],
  };
  const t = rotate(BANK[kind], `pick-${kind}|${key}`, seq);
  const text = fillSlots(t.item, { s, name, pos: place ? ordWord(place) : 'out of the race', Pos: place ? cap(ordWord(place)) : 'Out of the race' });
  return { text: text[0].toUpperCase() + text.slice(1), tpl: `pick-${kind}#${t.index}` };
}

// ---------------------------------------------------------------------------
// His own past — cited from the cast table, nowhere else
// ---------------------------------------------------------------------------

export function pastFacts(cast: Cast): Fact[] {
  const p = cast.past;
  const who = cast.analyst.name;
  return [
    fact('booth', `${who} drove ${p.seasons} seasons and won ${p.wins} races`, p.wins),
    fact('booth', `${who} drove ${p.seasons} seasons`, p.seasons),
    fact('booth', `${who} won ${p.strongWins} races at ${p.strongKind.toLowerCase()}s`, p.strongWins),
    fact('booth', `${who}'s old rival was ${p.rival}, for ${p.rivalYears} seasons`, p.rivalYears),
    ...(p.titles > 0 ? [fact('booth', `${who} won ${plural(p.titles, 'title')}`, p.titles)] : []),
  ];
}

const KIND_WORDS: Record<AnalystPast['strongKind'], string> = {
  'Short Track': 'short tracks',
  Superspeedway: 'big superspeedways',
  'Intermediate Track': 'mile-and-a-halves',
  'Road Course': 'road courses',
};

/** A story from his own career, matched to where they are today. */
export function anecdote(cast: Cast, category: string, key: string, seq: number): { text: string; tpl: string } {
  const p = cast.past;
  const home = category === p.strongKind;
  const bank = home
    ? [
        `I won ${p.strongWins} of my ${p.wins} races on the ${KIND_WORDS[p.strongKind]}. A place like this, you can feel the race come to you.`,
        `This is my kind of racetrack. ${p.strongWins} of my wins came on the ${KIND_WORDS[p.strongKind]}, and every one of them was about patience.`,
        `I spent ${p.seasons} seasons learning tracks like this one. You don't win here in the first half.`,
      ]
    : [
        `I drove ${p.seasons} seasons and this kind of place never once came easy to me. Watch who stays out of trouble.`,
        `I won ${p.wins} races and I'll be honest, most of them were not at tracks like this one. Patience pays here.`,
        `In ${p.seasons} seasons I learned one thing: the first half of a race is for staying out of trouble.`,
      ];
  const t = rotate(bank, `anecdote|${key}`, seq);
  return { text: t.item, tpl: `anecdote#${home ? 'h' : 'a'}${t.index}` };
}

/** Two drivers at each other remind him of his own war. */
export function rivalMemory(cast: Cast, a: string, b: string, key: string, seq: number): { text: string; tpl: string } {
  const p = cast.past;
  const bank = [
    `${a} and ${b} remind me of me and ${p.rival}. ${p.rivalYears} seasons and we never said a kind word off the track.`,
    `I had one of those. ${p.rival} and I went at it for ${p.rivalYears} seasons. It never ends the way you think it will.`,
    `That is how it started with me and ${p.rival}. ${p.rivalYears} seasons of that. You never forget a man like that.`,
  ];
  const t = rotate(bank, `rivalmem|${key}`, seq);
  return { text: t.item, tpl: `rivalmem#${t.index}` };
}

/** The driving he has no time for, said when the dice serve him an example. */
export function grudgeLine(cast: Cast, key: string, seq: number): { text: string; tpl: string } {
  const BANK: Record<AnalystPast['grudge'], string[]> = {
    wreckers: [
      "That's the kind of driving I never had time for. You don't have to wreck a man to beat him.",
      "I'll say it every time: wrecking somebody isn't racing him.",
      'I raced a lot of years and never once needed to put a man in the wall to pass him.',
      'You want to beat a man, beat him. Wrecking him is something else.',
      "That one bothers me. That's not hard racing, that's just wrecking.",
    ],
    hotheads: [
      'Temper. I lost more races to my own temper than to any driver out there.',
      "You can't race angry. I tried it for years and it never once worked.",
      'Somebody needs to cool off, and I say that as a man who never could.',
      'Hot heads make for a short afternoon. I learned that the hard way.',
      'Racing mad gets you exactly this.',
    ],
    'pit-road': [
      "I hate seeing a race given away on pit road. You can't win it there, but you can sure lose it.",
      'Pit road. Every time. You do all that work on track and hand it back down there.',
      'That is a race lost on pit road, and those are the ones that keep you up at night.',
      'You cannot make that up on the racetrack. Not today.',
      'Pit road giveth and pit road taketh away. Mostly it taketh.',
    ],
    restarts: [
      'I never liked a restart. Everybody goes at once and somebody always pays for it.',
      'Restarts are where good days go bad. I lived it.',
      'Every restart is a coin flip with somebody else holding the coin.',
      'Nobody gets out of a restart clean every time. Nobody.',
      'I hated restarts when I drove and I hate them from up here too.',
    ],
  };

  const bank = BANK[cast.past.grudge];
  const t = rotate(bank, `grudge|${key}`, seq);
  return { text: t.item, tpl: `grudge-${cast.past.grudge}#${t.index}` };
}
